import { Button, Input, Layout, message, Popconfirm, Table, Tag, Tooltip } from 'antd';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { Utils } from '../../../common/services/Utils';
import LayoutHeader from '../../../../components/LayoutHeader';
import { TestProjectsVisualStore, TestProjectWizardStore } from '../../stores';
import { TestProject, TestProjectListModel } from '../../types';
import { TestProjectCreationWizard } from '..';
import { ColumnProps } from 'antd/lib/table';
import { CopyOutlined, ExclamationCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { Observer } from 'mobx-react';
import TimeElapsed from '../TimeElapsed';
import TestProjectProgressBar from '../TestProjectProgressBar';
import TestProjectFilter from '../TestProjectFilter';
import TestProjectTagsFilter from './TestProjectTagsFilter';
import MinDocCountFilter from './MinDocCountFilter';
import BaselineStatusFilter from './BaselineStatusFilter';
import { SorterResult } from 'antd/lib/table/interface';
import { OrderBy } from '../../../common/types';
import { BaselineCoverageCategory, TestProjectUtils } from '../../misc';
import './TestProjectsList.less';

const { Content } = Layout;

type Props = {
    store: TestProjectsVisualStore;
    wizardStore: TestProjectWizardStore;
};

const TestProjectsList: React.FC<Props> = ({ store, wizardStore }) => {
    React.useEffect(() => {
        if (store.currentProject) {
            store.loadTestProjects();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [store.currentProject]);

    const getRecordError = (record: TestProjectListModel) => {
        const isInvalid = record.hasValidPackages === false || record.hasValidTopics === false;
        const hasErrorMessage = record.errors && record.errors.length > 0;
        if (isInvalid && hasErrorMessage) {
            return (
                <Tooltip title="Issues detected. Open the project to review details.">
                    <ExclamationCircleOutlined style={{ color: '#B80C02', fontSize: '18px' }} />
                </Tooltip>
            );
        }

        return null;
    };

    const getRecordRunControls = (record: TestProjectListModel) => {
        if (record.isAborting === true) {
            return <span style={{ color: '#B80C02' }}>Aborting...</span>;
        }
        return (
            <>
                {!record.isAborting && record.isRunning && record.lastRunTime && (
                    <div style={{ minWidth: '100px' }}>
                        <TestProjectProgressBar
                            executedCount={record.processedPackageCount}
                            totalCount={record.packageIds.length}
                        />
                    </div>
                )}

                {record.isRunning !== true && record.hasValidBaselines && (
                    <Tooltip title="Run project">
                        <Button
                            data-id={`test-projects-run-${record.id}`}
                            type="link"
                            size="small"
                            onClick={e => handleRunTestClick(e, record.id)}
                            style={{ opacity: 1 }}
                            icon={<i style={{ verticalAlign: 'bottom' }} className="alpha-icon md play-icon green" />}
                        />
                    </Tooltip>
                )}
            </>
        );
    };

    const goToTestProjectLastRun = (testProjectId: string, runId: string) => {
        store.goToTestResults(testProjectId, runId);
    };

    const getLastRunContent = (record: TestProjectListModel) => {
        if (record.isAborting === true) {
            return null;
        }
        if (record.lastRunTime && record.isRunning === true) {
            return (
                <span style={{ marginLeft: '-8px' }}>
                    <TimeElapsed datetime={record.lastRunTime} />
                </span>
            );
        }

        if (!record.lastRunId || record.lastRunTime == null || record.lastRunFuzzy == null) {
            return null;
        }

        return (
            <Button
                className="link-button"
                type="link"
                size="small"
                onClick={e => {
                    e.stopPropagation();
                    goToTestProjectLastRun(record.id, record.lastRunId!);
                }}
                style={{ marginLeft: -16 }}
            >
                <span data-id-cells="lastRunTime">{Utils.formatDateStringShort(record.lastRunTime, true)}</span>
            </Button>
        );
    };

    const getLastRunFuzzyPercent = (record: TestProjectListModel) => {
        if (
            !record.lastRunId ||
            record.isRunning === true ||
            record.lastRunTime == null ||
            record.lastRunFuzzy == null
        ) {
            return null;
        }
        return Math.round(record.lastRunFuzzy + Number.EPSILON);
    };

    const getTruncatedError = (error: string, maxLength = 1000) => {
        return error.length > maxLength ? error.slice(0, maxLength) + '...' : error;
    };
    const getBaselineCoverageDisplay = (record: TestProjectListModel) => {
        const baselineCategory = TestProjectUtils.getBaselineCoverageCategory(record.baselineCoverage);
        const percentage = TestProjectUtils.getRoundedBaselineCoverage(record.baselineCoverage);

        if (baselineCategory === BaselineCoverageCategory.NOT_AVAILABLE || percentage == null) {
            return <span className="baseline-coverage-value baseline-coverage-na">—</span>;
        }

        if (baselineCategory === BaselineCoverageCategory.EMPTY_BASELINES) {
            return <span className="baseline-coverage-value baseline-coverage-red">{percentage}%</span>;
        }

        if (baselineCategory === BaselineCoverageCategory.LOW_COVERAGE) {
            return <span className="baseline-coverage-value baseline-coverage-amber">{percentage}%</span>;
        }

        return <span className="baseline-coverage-value baseline-coverage-default">{percentage}%</span>;
    };

    const handleRunTestClick = (e: React.MouseEvent<HTMLElement, MouseEvent>, testProjectId: string) => {
        e.stopPropagation();

        const testProject = store.testProjects.find(tp => tp.id === testProjectId);

        if (testProject && testProject.hasValidTopics === false) {
            message.warning(
                'This test project has invalid topics. Please open the project and clean it before running.'
            );
            return;
        }

        store.runTestProject(testProjectId);
    };

    const getTagColor = (tag: string): string => {
        const colors = [
            'magenta',
            'red',
            'volcano',
            'orange',
            'gold',
            'lime',
            'green',
            'cyan',
            'blue',
            'geekblue',
            'purple'
        ];

        let hash = 0;
        for (let i = 0; i < tag.length; i++) {
            hash = tag.charCodeAt(i) + hash * 31;
        }
        const index = Math.abs(hash) % colors.length;
        return colors[index];
    };

    const getSorterOrder = (columnKey: string) => {
        if (!store.orderBy || store.orderBy.field !== columnKey) {
            return undefined;
        }

        return store.orderBy.direction === 'ASC' ? 'ascend' : 'descend';
    };

    const sortByLastRunFuzzy = (a: TestProjectListModel, b: TestProjectListModel) => {
        const getCategory = (record: TestProjectListModel) => {
            if (record.lastRunId && record.lastRunError && record.lastRunError.length > 0) {
                return 'error';
            }
            if (record.lastRunId && record.lastRunTime != null && record.lastRunFuzzy != null) {
                return 'numeric';
            }
            return 'empty';
        };

        const categoryA = getCategory(a);
        const categoryB = getCategory(b);

        const categoryOrder: Record<string, number> = { error: 0, empty: 1, numeric: 2 };

        if (categoryA !== categoryB) {
            return categoryOrder[categoryA] - categoryOrder[categoryB];
        }

        if (categoryA === 'numeric' && categoryB === 'numeric') {
            return a.lastRunFuzzy! - b.lastRunFuzzy!;
        }

        return 0;
    };

    const columns: ColumnProps<TestProjectListModel>[] = [
        {
            key: 'name',
            dataIndex: 'name',
            title: 'Name',
            width: 300,
            sorter: (a: TestProjectListModel, b: TestProjectListModel) => Utils.safeLocaleCompare(a.name, b.name),
            sortOrder: getSorterOrder('name'),
            render: (text: string, record: TestProjectListModel) => (
                <div className="test-project-list-name-container">
                    <span data-id-cells="name">{record.name}</span>
                    {record.userTags && record.userTags.length > 0 && (
                        <div className="project-tags-container">
                            {record.userTags.slice(0, 3).map(tag => (
                                <Tooltip key={tag} title={tag}>
                                    <Tag color={getTagColor(tag)} className="project-tag">
                                        {tag}
                                    </Tag>
                                </Tooltip>
                            ))}
                            {record.userTags.length > 3 && (
                                <Tooltip title={record.userTags.slice(3).join(', ')}>
                                    <Tag className="project-tag-overflow">+{record.userTags.length - 3}</Tag>
                                </Tooltip>
                            )}
                        </div>
                    )}
                </div>
            )
        },
        {
            key: 'createdTime',
            dataIndex: 'createdTime',
            title: 'Created time',
            className: 'col-fixed-width',
            sorter: (a: TestProjectListModel, b: TestProjectListModel) =>
                Utils.safeDateStringCompare(a.createdTime, b.createdTime),
            sortOrder: getSorterOrder('createdTime'),
            ellipsis: true,
            render: (date: string) => (
                <span style={{ color: '#999', fontSize: '0.8rem' }} data-id-cells="createdTime">
                    {Utils.formatDateStringShort(date, true)}
                </span>
            ),
            width: 120
        },
        {
            key: 'createdBy',
            dataIndex: 'createdBy',
            title: 'Created by',
            className: 'col-fixed-width',
            sorter: (a: TestProjectListModel, b: TestProjectListModel) =>
                Utils.safeLocaleCompare(store.getUserNameById(a.createdBy), store.getUserNameById(b.createdBy)),
            sortOrder: getSorterOrder('createdBy'),
            ellipsis: true,
            render: (createdBy: string) => (
                <Observer>
                    {() => (
                        <span
                            style={{ color: '#999', fontSize: '0.8rem' }}
                            data-id-cells="createdBy"
                            title={store.getUserNameById(createdBy)}
                        >
                            {store.getUserNameById(createdBy)}
                        </span>
                    )}
                </Observer>
            ),
            width: 140
        },

        {
            key: 'updatedBy',
            dataIndex: 'updatedBy',
            title: 'Updated by',
            className: 'col-fixed-width',
            sorter: (a: TestProjectListModel, b: TestProjectListModel) =>
                Utils.safeLocaleCompare(store.getUserNameById(a.updatedBy), store.getUserNameById(b.updatedBy)),
            sortOrder: getSorterOrder('updatedBy'),
            ellipsis: true,
            render: (updatedBy: string) => (
                <Observer>
                    {() => (
                        <span
                            style={{ color: '#999', fontSize: '0.8rem' }}
                            data-id-cells="updatedBy"
                            title={store.getUserNameById(updatedBy)}
                        >
                            {store.getUserNameById(updatedBy)}
                        </span>
                    )}
                </Observer>
            ),
            width: 140
        },
        {
            key: 'lastRunId',
            dataIndex: 'lastRunId',
            title: 'Last run',
            className: 'col-fixed-width',
            sorter: (a: TestProjectListModel, b: TestProjectListModel) =>
                Utils.safeDateStringCompare(a.lastRunTime, b.lastRunTime),
            sortOrder: getSorterOrder('lastRunId'),
            ellipsis: true,
            render: (_: string, record: TestProjectListModel) => getLastRunContent(record),
            width: 100
        },
        {
            key: 'baselineCoverage',
            dataIndex: 'baselineCoverage',
            title: 'Baseline',
            sorter: (a: TestProjectListModel, b: TestProjectListModel) => {
                const aCoverage = TestProjectUtils.getRoundedBaselineCoverage(a.baselineCoverage) ?? -1;
                const bCoverage = TestProjectUtils.getRoundedBaselineCoverage(b.baselineCoverage) ?? -1;
                return aCoverage - bCoverage;
            },
            sortOrder: getSorterOrder('baselineCoverage'),
            render: (_: string, record: TestProjectListModel) => getBaselineCoverageDisplay(record),
            width: 75
        },
        {
            key: 'lastRunFuzzy',
            dataIndex: 'lastRunFuzzy',
            title: 'Result',
            width: 70,
            sorter: sortByLastRunFuzzy,
            sortOrder: getSorterOrder('lastRunFuzzy'),
            render: (_: string, record: TestProjectListModel) => {
                const lastRunFuzzy = getLastRunFuzzyPercent(record);

                if (record.lastRunId && record.lastRunError && record.lastRunError.length > 0) {
                    return (
                        <Tooltip title={getTruncatedError(record.lastRunError)}>
                            <WarningOutlined style={{ color: 'red' }} />
                        </Tooltip>
                    );
                }

                if (lastRunFuzzy == null) {
                    return null;
                }

                const baselineCategory = TestProjectUtils.getBaselineCoverageCategory(record.baselineCoverage);
                const isMuted =
                    baselineCategory === BaselineCoverageCategory.EMPTY_BASELINES ||
                    baselineCategory === BaselineCoverageCategory.LOW_COVERAGE;

                if (isMuted) {
                    return (
                        <span data-id-cells="lastRunFuzzy" className="result-value-muted">
                            ({lastRunFuzzy}%)
                        </span>
                    );
                }

                const getResultStyle = (percentage: number) => {
                    if (percentage >= 90) {
                        return { color: '#52c41a', fontWeight: 500 }; // Green for 90% and above
                    }
                    if (percentage >= 80) {
                        return { color: '#faad14', fontWeight: 500 }; // Orange for 80-90%
                    }
                    return { color: '#f5222d', fontWeight: 500 }; // Red for below 80%
                };

                return (
                    <span data-id-cells="lastRunFuzzy" style={getResultStyle(lastRunFuzzy)}>
                        {lastRunFuzzy}%
                    </span>
                );
            }
        },
        {
            key: 'lastRunDocCount',
            dataIndex: 'lastRunDocCount',
            title: 'Docs',
            sorter: (a: TestProjectListModel, b: TestProjectListModel) => {
                const aCount = a.lastRunDocCount ?? -1;
                const bCount = b.lastRunDocCount ?? -1;
                return aCount - bCount;
            },
            sortOrder: getSorterOrder('lastRunDocCount'),
            render: (_: string, record: TestProjectListModel) => {
                if (!record.lastRunId || record.lastRunDocCount == null) {
                    return null;
                }

                return <span data-id-cells="lastRunDocCount">{record.lastRunDocCount}</span>;
            },
            width: 70
        },
        {
            key: 'warning',
            dataIndex: 'warning',
            title: '',
            width: 40,
            render: (_: unknown, record: TestProjectListModel) => getRecordError(record)
        },
        {
            key: 'isRunning',
            dataIndex: 'isRunning',
            width: 120,
            title: '',
            render: (_: unknown, record: TestProjectListModel) => getRecordRunControls(record)
        },
        {
            key: 'actions',
            dataIndex: 'actions',
            title: 'Actions',
            width: 120,
            render: (_: unknown, record: TestProjectListModel) => (
                <div data-id-cells="actions" style={{ minWidth: 100 }} onClick={e => e.stopPropagation()}>
                    <Observer>
                        {() => (
                            <Tooltip title="Create a copy of this test project">
                                <Button
                                    type="link"
                                    size="small"
                                    data-id="button-copy-test-project"
                                    icon={<CopyOutlined style={{ color: '#9ba0ab' }} />}
                                    style={{ marginRight: 8, verticalAlign: 'text-bottom' }}
                                    disabled={store.isCopyingTestProject}
                                    loading={store.isCopyingTestProject}
                                    onClick={() => store.createCopyOfTestProject(record.id)}
                                />
                            </Tooltip>
                        )}
                    </Observer>
                    <Tooltip title="Edit project">
                        <Button
                            type="link"
                            size="small"
                            data-id="button-edit-test-project"
                            icon={<i className="alpha-icon lg edit-icon" />}
                            style={{ marginRight: 8 }}
                            onClick={() => editTestProject(record.id)}
                        />
                    </Tooltip>
                    <Popconfirm
                        title="Are you sure you want to delete this test project?"
                        onConfirm={() => store.deleteTestProject(record.id)}
                    >
                        <Tooltip title="Delete project">
                            <Button
                                type="link"
                                size="small"
                                data-id="button-edit-test-project"
                                icon={<i className="alpha-icon lg delete-icon-red" />}
                            />
                        </Tooltip>
                    </Popconfirm>
                </div>
            )
        }
    ];

    const editTestProject = (testProjectId: string) => {
        wizardStore.setUpTestProject(testProjectId);
        wizardStore.setIsCreationWizardVisible(true);
    };

    return (
        <Layout className="screen-size" style={{ ...{ height: '100%', background: 'white' } }}>
            <LayoutHeader
                subtitle={Utils.getSubtitle(store!.currentProject)}
                helpMessage="Isolated page for controlled experimentation"
                title="Test projects"
                buttons={[
                    <Button
                        key="test-projects-add"
                        data-id="button-add-test-project"
                        type="primary"
                        size="large"
                        onClick={() => {
                            wizardStore.setIsCreationWizardVisible(true);
                        }}
                    >
                        Create new test project
                    </Button>
                ]}
            />
            <Layout>
                <div style={{ marginBottom: 12, display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <Input.Search
                        placeholder="Search test projects..."
                        value={store.searchValue}
                        onChange={e => store.setSearchValue(e.target.value)}
                        allowClear
                        onSearch={val => store.setSearchValue(val)}
                        style={{ width: 300 }}
                    />
                    <TestProjectFilter value={store.filterValue} onChange={store.setFilterValue} />
                    <BaselineStatusFilter
                        value={store.baselineStatusFilterValue}
                        onChange={store.setBaselineStatusFilterValue}
                    />
                    <TestProjectTagsFilter
                        availableTagsWithCounts={store.availableTestProjectTagsWithCounts}
                        selectedTags={store.selectedTagsFilter}
                        onChange={store.setSelectedTagsFilter}
                    />
                    <MinDocCountFilter value={store.minDocCount} onChange={store.setMinDocCount} />
                </div>
                <TestProjectCreationWizard store={wizardStore} />
                <Content style={{ maxHeight: 'calc(100vh - 170px)', overflowY: 'auto' }}>
                    <Table
                        data-id="test-projects-list"
                        className="alpha-portal-table test-projects-list-table"
                        dataSource={store.filteredProjects}
                        loading={store.isLoadingProjects || store.isLoadingUsers}
                        columns={columns}
                        pagination={false}
                        sticky
                        rowKey={(record: TestProjectListModel) => record.id}
                        onChange={(_, __, sorter: SorterResult<TestProjectListModel>) => {
                            let orderBy: OrderBy | undefined;

                            if (sorter.columnKey && sorter.order) {
                                orderBy = {
                                    field: sorter.columnKey as string,
                                    direction: sorter.order === 'ascend' ? 'ASC' : 'DESC'
                                };
                            }

                            store.setOrderBy(orderBy);
                        }}
                        scroll={{ x: 'max-content' }}
                        onRow={(record: TestProject) => {
                            return {
                                style: { height: 48 },
                                onClick: () => store.goToTestProjectDashboard(record.id)
                            };
                        }}
                    />
                </Content>
            </Layout>
        </Layout>
    );
};

export default observer(TestProjectsList);
